import { FC } from "react";

import { NotificationEmailOpened } from "./types/email-opened";
import { NotificationMissedCall } from "./types/missed-call";
import { NotificationCampaignApproved } from "./types/campaign-approved";
import { NotificationCampaignRejected } from "./types/campaign-rejected";
import { NotificationAuditionRetry } from "./types/audition-retry";
import { NotificationMeetingQualified } from "./types/meeting-qualified";
import { NotificationMeetingUnqualified } from "./types/meeting-unqualified";
import { NotificationMeetingRescheduled } from "./types/meeting-rescheduled";
import { NotificationMeetingCanceled } from "./types/meeting-canceled";
import { NotificationMeetingNoShow } from "./types/meeting-no-show";
import { NotificationMeetingShown } from "./types/meeting-shown";
import { NotificationMeetingPaidOut } from "./types/meeting-paid-out";
import { NotificationMeetingDisputed } from "./types/meeting-disputed";
import { NotificationMeetingDisputePaidOut } from "./types/meeting-dispute-paid-out";
import { NotificationMeetingDisputeNotPaidOut } from "./types/meeting-dispute-not-paid-out";
import { NotificationEmailReceived } from "./types/email-received";
import { NotificationColdOutreachEmailResponded } from "./types/cold-outreach-email-responded";

import { NotificationI } from "@/lib/types/notification";
import { ASSESSMENT_STATUS_TYPES } from "@/constants/assessment";
import { DISPOSITIONS } from "shared/lib/constants/dispositions";
import { NotificationExclusiveListReceived } from "@/modules/notifications/notification/types/exclusive-list-received";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { NotificationCustomerForumPost } from "@/modules/notifications/notification/types/customer-forum-post";
import { NOTIFICATION_TYPES } from "shared/lib/constants/notifications";
import { NotificationCalendarInviteReceived } from "@/modules/notifications/notification/types/calendar-invite-received";
import { NotificationDialBlockChanged } from "@/modules/notifications/notification/types/dial-block-changed";
import { NotificationDialBlockAvailability } from "@/modules/notifications/notification/types/dial-block-availability";
import { NotificationDialBlockStrikeAlert } from "@/modules/notifications/notification/types/dial-block-strike-alert";
import {
  NotificationTopTenDigest,
  NotificationTopTenDue,
} from "@/modules/notifications/notification/types/top-ten";
import { NotificationPipelineReminder } from "@/modules/notifications/notification/types/pipeline-reminder";
import { NotificationDialBlockRequestWaiting } from "@/modules/notifications/notification/types/dial-block-request-waiting";
import { NotificationIncentive } from "@/modules/notifications/notification/types/incentive";

export type NotificationPropsI = NotificationI & PropsWithClassNameI;

export const Notification: FC<NotificationPropsI> = (props) => {
  const { notification_type, audition_status, meeting_feedback } = props || {};

  switch (notification_type) {
    case NOTIFICATION_TYPES.CUSTOMER_FORUM_POST:
      return <NotificationCustomerForumPost {...props} />;
    case NOTIFICATION_TYPES.EMAIL_OPENED:
      return <NotificationEmailOpened {...props} />;
    case NOTIFICATION_TYPES.EMAIL_RECEIVED:
      return <NotificationEmailReceived {...props} />;
    case NOTIFICATION_TYPES.MISSED_CALL:
      return <NotificationMissedCall {...props} />;
    case NOTIFICATION_TYPES.AUDITION:
      switch (audition_status) {
        case ASSESSMENT_STATUS_TYPES.SUCCESS:
          return <NotificationCampaignApproved {...props} />;
        case ASSESSMENT_STATUS_TYPES.BLOCKED:
          return <NotificationCampaignRejected {...props} />;
        case ASSESSMENT_STATUS_TYPES.RETRY:
          return <NotificationAuditionRetry {...props} />;
      }
      break;
    case NOTIFICATION_TYPES.LIST_RECEIVED:
      return <NotificationExclusiveListReceived {...props} />;
    case NOTIFICATION_TYPES.MEETING:
      switch (meeting_feedback) {
        case DISPOSITIONS.MEETING_QUALIFIED:
          return <NotificationMeetingQualified {...props} />;
        case DISPOSITIONS.MEETING_NOT_QUALIFIED:
          return <NotificationMeetingUnqualified {...props} />;
        case DISPOSITIONS.MEETING_RESCHEDULED:
          return <NotificationMeetingRescheduled {...props} />;
        case DISPOSITIONS.CANCELLED_MEETING:
          return <NotificationMeetingCanceled {...props} />;
        case DISPOSITIONS.MEETING_NO_SHOW:
          return <NotificationMeetingNoShow {...props} />;
        case DISPOSITIONS.MEETING_SHOWN:
          return <NotificationMeetingShown {...props} />;
        case DISPOSITIONS.MEETING_PAID_OUT:
          return <NotificationMeetingPaidOut {...props} />;
        case DISPOSITIONS.MEETING_DISPUTED:
          return <NotificationMeetingDisputed {...props} />;
        case DISPOSITIONS.MEETING_DISPUTE_PAID_OUT:
          return <NotificationMeetingDisputePaidOut {...props} />;
        case DISPOSITIONS.MEETING_DISPUTE_NOT_PAID_OUT:
          return <NotificationMeetingDisputeNotPaidOut {...props} />;
      }
      break;
    case NOTIFICATION_TYPES.COLD_OUTREACH_EMAIL_RESPONDED:
      return <NotificationColdOutreachEmailResponded {...props} />;
    case NOTIFICATION_TYPES.CALENDAR_INVITE_RECEIVED:
      return <NotificationCalendarInviteReceived {...props} />;
    case NOTIFICATION_TYPES.DIAL_BLOCK_CHANGED:
      return <NotificationDialBlockChanged {...props} />;
    case NOTIFICATION_TYPES.DIAL_BLOCK_AVAILABILITY_APPROVED:
    case NOTIFICATION_TYPES.DIAL_BLOCK_AVAILABILITY_DENIED:
      return <NotificationDialBlockAvailability {...props} />;
    case NOTIFICATION_TYPES.DIAL_BLOCK_STRIKE_ALERT:
      return <NotificationDialBlockStrikeAlert {...props} />;
    case NOTIFICATION_TYPES.DIAL_BLOCK_REQUEST_WAITING:
      return <NotificationDialBlockRequestWaiting {...props} />;
    case NOTIFICATION_TYPES.INCENTIVE_STARTED:
    case NOTIFICATION_TYPES.INCENTIVE_WON:
      return <NotificationIncentive {...props} />;
    case NOTIFICATION_TYPES.TOP_TEN_DUE:
      return <NotificationTopTenDue {...props} />;
    case NOTIFICATION_TYPES.TOP_TEN_DIGEST:
      return <NotificationTopTenDigest {...props} />;
    case NOTIFICATION_TYPES.PIPELINE_REMINDER:
      return <NotificationPipelineReminder {...props} />;
  }

  return null;
};
